import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import type { ChannelUsageStats } from '@easyflow/shared';
import { 
  Loader2, 
  BarChart3, 
  Phone, 
  Clock, 
  AlertTriangle, 
  TrendingUp,
  RefreshCw
} from 'lucide-react';

interface ChannelAnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  tenant: {
    id: string;
    name: string;
  } | null;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export function ChannelAnalyticsModal({ open, onClose, tenant }: ChannelAnalyticsModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<ChannelUsageStats | null>(null);

  useEffect(() => {
    if (open && tenant) {
      void loadAnalytics();
    }
  }, [open, tenant]);

  const loadAnalytics = async () => {
    if (!tenant) return;
    
    setLoading(true);
    setStats(null);
    try {
      const response = await apiClient.get<ChannelUsageStats>(`/master/tenants/${tenant.id}/channel-analytics`);
      if (!response.data) throw new Error('Analytics response unavailable');
      setStats(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load analytics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const verified = stats?.counter.state === 'verified';
  const usagePercent = stats && stats.max_channels > 0 ? Math.round((stats.current_channels / stats.max_channels) * 100) : 0;
  const usageColor = !verified ? 'yellow' : usagePercent >= 90 ? 'red' : usagePercent >= 70 ? 'yellow' : 'green';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Channel Analytics
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => { void loadAnalytics(); }} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <DialogDescription>
            Usage statistics for <strong>{tenant?.name}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          </div>
        ) : stats ? (
          <div className="space-y-6 py-4">
            <div className="relative p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-full ${
                    usageColor === 'red' ? 'bg-red-100' :
                    usageColor === 'yellow' ? 'bg-yellow-100' : 'bg-green-100'
                  }`}>
                    <Phone className={`h-6 w-6 ${
                      usageColor === 'red' ? 'text-red-600' :
                      usageColor === 'yellow' ? 'text-yellow-600' : 'text-green-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-foreground">Real-time Usage</p>
                    <p className="text-sm text-muted-foreground">Active channels now</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold text-foreground">
                    {verified ? stats.current_channels : '—'}
                    <span className="text-lg text-muted-foreground/70">/{stats.max_channels}</span>
                  </p>
                  <p className={`text-sm font-medium ${
                    usageColor === 'red' ? 'text-red-600' :
                    usageColor === 'yellow' ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {verified ? `${usagePercent}% capacity` : 'Usage unavailable / unverified'}
                  </p>
                </div>
              </div>
              {verified && <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all ${
                    usageColor === 'red' ? 'bg-red-500' :
                    usageColor === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
              </div>}
              <p className={`text-xs mt-2 ${verified ? 'text-muted-foreground' : 'text-amber-700'}`}>
                {stats.counter.scope === 'cluster' ? 'Shared across nodes' : 'Local process only'}
                {' · '}{verified ? 'Verified' : 'Degraded'}
                {stats.counter.reason && ` · ${stats.counter.reason.replace(/_/g, ' ')}`}
                {!verified && ' · Global usage cannot be verified'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-foreground">Today</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Talk Time</span>
                    <span className="text-sm font-semibold">{formatDuration(stats.total_seconds_today)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Peak Channels</span>
                    <span className="text-sm font-semibold">{stats.peak_channels_today}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Over-limit Events</span>
                    <span className={`text-sm font-semibold ${stats.over_limit_count_today > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {stats.over_limit_count_today}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-lg border shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-foreground">This Month</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Talk Time</span>
                    <span className="text-sm font-semibold">{formatDuration(stats.total_seconds_month)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Peak Channels</span>
                    <span className="text-sm font-semibold">{stats.peak_channels_month}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Over-limit Events</span>
                    <span className={`text-sm font-semibold ${stats.over_limit_count_month > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {stats.over_limit_count_month}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {stats.over_limit_count_month > 0 && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {stats.over_limit_count_month} calls were rejected this month
                  </p>
                  <p className="text-xs text-amber-600">
                    Consider increasing the channel limit to avoid losing calls.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : <p className="py-4 text-sm text-muted-foreground">Analytics unavailable. Refresh to retry.</p>}
      </DialogContent>
    </Dialog>
  );
}
